/**
 * session.js - the signed-in user's Entra ID token for API calls, and what
 * happens when it can no longer be minted.
 *
 * An account can stay cached after its refresh token has ended (the SPA
 * refresh token lives 24 h, and the silent iframe renewal is blocked by
 * third-party-cookie rules). Then every API call went out with no token, the
 * server answered 401 and every view rendered empty until the user signed out
 * and in. Now that state raises SESSION_EXPIRED and SessionExpiredBar asks the
 * user to Continue (one user-initiated redirect; an automatic one could lose
 * unsaved edits). While expired, MSAL is asked again only after a back-off.
 */
import { msalInstance, loginRequest } from './msalConfig';

export const SESSION_EXPIRED = 'gops:session-expired';
export const SESSION_OK = 'gops:session-ok';
const RETURN_KEY = 'gops_return_hash';

// Acquire tokens for the SAME scopes the interactive login used. A mismatched
// scope set makes acquireTokenSilent miss the cache and attempt a silent iframe
// renewal, which Chrome/Edge block. Matching scopes keeps this a cache hit, and
// expiry renews via the refresh token (a plain CORS fetch, not cookie-gated).
const TOKEN_SCOPES = loginRequest.scopes;

// MSAL renews on ACCESS-token expiry, so acquireTokenSilent can hand back an
// ID token that is itself already expired. Treat one whose exp is within
// 2 min (or unparseable) as stale.
const isStale = (tok) => {
  try {
    return JSON.parse(atob(tok.split('.')[1])).exp * 1000 < Date.now() + 120000;
  } catch {
    return true;
  }
};

// Being offline is not an expired sign-in; don't ask the user to re-auth for it.
const NETWORK_CODES = ['no_network_connectivity', 'network_error', 'endpoints_resolution_error'];

const BACKOFF_MIN = 15000;
const BACKOFF_MAX = 5 * 60000;
let expired = false;
let backoff = 0;
let nextTryAt = 0;

export const isSessionExpired = () => expired;

function markExpired(code) {
  backoff = Math.min(backoff ? backoff * 2 : BACKOFF_MIN, BACKOFF_MAX);
  nextTryAt = Date.now() + backoff;
  if (expired) return;
  expired = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED, { detail: { code: code || 'no_token' } }));
}

function markOk() {
  backoff = 0;
  nextTryAt = 0;
  if (!expired) return;
  expired = false;
  window.dispatchEvent(new CustomEvent(SESSION_OK));
}

// Single-flight token getter shared by the opening burst of API calls. Never
// pins a null (failed) result: a short dedupe window collapses concurrent
// callers, then the next call may retry (after the back-off when expired).
let tokenPromise = null;
let pending = false;
export const getIdToken = (forceRefresh = false) => {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return Promise.resolve(null);
  if (pending || (tokenPromise && !forceRefresh)) return tokenPromise; // one MSAL call at a time
  if (expired && Date.now() < nextTryAt) return Promise.resolve(null);
  pending = true;
  tokenPromise = msalInstance
    .acquireTokenSilent({ scopes: TOKEN_SCOPES, account, forceRefresh })
    .then(async (r) => {
      let tok = r && r.idToken;
      if (tok && isStale(tok) && !forceRefresh) {
        const r2 = await msalInstance.acquireTokenSilent({ scopes: TOKEN_SCOPES, account, forceRefresh: true });
        tok = r2 && r2.idToken;
      }
      if (!tok) throw Object.assign(new Error('no id token'), { errorCode: 'no_token' });
      markOk();
      return tok;
    })
    .catch((e) => {
      const code = e && e.errorCode;
      if (!NETWORK_CODES.includes(code) && msalInstance.getAllAccounts()[0]) markExpired(code);
      return null;
    })
    .finally(() => {
      pending = false;
    });
  const p = tokenPromise;
  setTimeout(() => {
    if (tokenPromise === p) tokenPromise = null;
  }, 2000);
  return tokenPromise;
};

/** Fetch wrapper: adds X-Client-Id and the bearer token to same-origin /api/ calls. */
export function makeAuthFetch(rawFetch, clientId) {
  return async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!url.startsWith('/api/')) return rawFetch(input, init);
    init.headers = { ...(init.headers || {}), 'X-Client-Id': clientId };
    const idToken = await getIdToken();
    if (idToken) init.headers = { ...init.headers, Authorization: `Bearer ${idToken}` };
    let res = await rawFetch(input, init);
    // 401 with a cached account: force one fresh token and retry once. If none
    // can be minted, getIdToken has raised SESSION_EXPIRED (the bar shows).
    if (res.status === 401 && msalInstance.getAllAccounts()[0]) {
      const fresh = await getIdToken(true);
      if (fresh) {
        // A valid token still rejected is a server config issue; don't loop.
        init.headers = { ...init.headers, Authorization: `Bearer ${fresh}` };
        res = await rawFetch(input, init);
      }
    }
    return res;
  };
}

// Proactive check when the tab comes back into view, at most once a minute,
// so the bar shows before the user clicks into an empty page.
const CHECK_EVERY = 60000;
let lastCheck = 0;
export function checkSession() {
  const now = Date.now();
  if (now - lastCheck < CHECK_EVERY) return false;
  lastCheck = now;
  getIdToken();
  return true;
}

export function watchSession() {
  const onVisible = () => {
    if (document.visibilityState === 'visible') checkSession();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', checkSession);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', checkSession);
  };
}

/** A stuck MSAL interaction flag makes the next redirect throw interaction_in_progress. */
function clearStuckInteraction() {
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      Object.keys(store)
        .filter((k) => k.startsWith('msal.') && k.endsWith('interaction.status'))
        .forEach((k) => store.removeItem(k));
    } catch {
      /* storage blocked */
    }
  }
}

/** The bar's Continue: one interactive redirect for the same account, back to the same page. */
export async function continueSignIn() {
  const account = msalInstance.getAllAccounts()[0];
  const hint = account ? { loginHint: account.username } : {};
  clearStuckInteraction();
  try {
    if (window.location.hash) sessionStorage.setItem(RETURN_KEY, window.location.hash);
  } catch {
    /* storage blocked: the user lands on the default page */
  }
  try {
    await msalInstance.acquireTokenRedirect({ ...loginRequest, account, ...hint });
  } catch (e) {
    console.error('acquireTokenRedirect failed, falling back to loginRedirect:', e);
    await msalInstance.loginRedirect({ ...loginRequest, ...hint });
  }
}

/** After handleRedirectPromise: put the user back on the page they were on. */
export function restoreReturnRoute() {
  try {
    const hash = sessionStorage.getItem(RETURN_KEY);
    if (!hash) return;
    sessionStorage.removeItem(RETURN_KEY);
    if (!window.location.hash || window.location.hash === '#') window.history.replaceState(null, '', hash);
  } catch {
    /* ignore */
  }
}

/** Test hook: forget all module state. */
export function _resetSessionForTests() {
  expired = false;
  backoff = 0;
  nextTryAt = 0;
  tokenPromise = null;
  pending = false;
  lastCheck = 0;
}
